import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import API_BASE_URL from '../config';
import Seo from '../components/Seo';
import { trackLead, trackEvent, trackContactClick } from '../utils/analytics';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [newsletterData, setNewsletterData] = useState({
    email: '',
    name: '',
  });
  const [loading, setLoading] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleNewsletterChange = (e) => {
    const { name, value } = e.target;
    setNewsletterData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/newsletter.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newsletterData),
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message,
          severity: 'success'
        });
        trackLead('newsletter');
        // Reset newsletter form
        setNewsletterData({ email: '', name: '' });
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Something went wrong. Please try again.',
          severity: 'error'
        });
        trackEvent('form_error', { form: 'newsletter' });
      }
    } catch (error) {
      console.error('Error submitting newsletter form:', error);
      setSnackbar({
        open: true,
        message: 'Network error. Please check your connection and try again.',
        severity: 'error'
      });
    } finally {
      setNewsletterLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/contact.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message,
          severity: 'success'
        });
        trackLead('contact');
        // Reset form
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Something went wrong. Please try again.',
          severity: 'error'
        });
        trackEvent('form_error', { form: 'contact' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbar({
        open: true,
        message: 'Network error. Please check your connection and try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ py: 8 }}>
      <Seo
        title="Contact Us — Request an Inspection or Quote"
        description="Contact InspectIQ for marine, cargo, container and project inspection services worldwide. Office in Vadodara, Gujarat. Call +91 95744 49544 or email info@inspectiq.in."
        path="/contact"
      />
      <Container maxWidth="lg">
        <Typography variant="h2" align="center" gutterBottom>
          Contact Us
        </Typography>
        <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 6 }}>
          We are here to help. Whether you need inspection services, have an inquiry, or want to explore
          collaboration opportunities, feel free to contact us. Fill out this simple form with your details,
          and we will get back to you as soon as possible.
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4 }}>
              <Typography variant="h4" gutterBottom>
                Get in Touch
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Give us a chance to serve and bring excellence to your inspection needs.
              </Typography>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Share your queries, requirements, or message in detail..."
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      sx={{ py: 1.5 }}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%' }}>
              <Typography variant="h4" gutterBottom>
                Contact Info
              </Typography>

              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography
                    variant="h6"
                    component="a"
                    href="tel:+919574449544"
                    onClick={() => trackContactClick('phone', '+919574449544')}
                    sx={{ color: 'text.primary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                  >
                    +91 95744 49544
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography
                    variant="h6"
                    component="a"
                    href="mailto:info@inspectiq.in"
                    onClick={() => trackContactClick('email', 'info@inspectiq.in')}
                    sx={{ color: 'text.primary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                  >
                    info@inspectiq.in
                  </Typography>
                </Box>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h5" gutterBottom>
                  Our Office Open Time (IST)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccessTimeIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography>Mon - Sat: 10:00 AM - 7:00 PM</Typography>
                    <Typography>Sunday: Closed</Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h5" gutterBottom>
                  Our Locations
                </Typography>
                <Box sx={{ display: 'flex', mb: 3 }}>
                  <LocationIcon sx={{ mr: 2, color: 'primary.main', mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                      Vadodara Office
                    </Typography>
                    <Typography>
                      Third Floor - 1, Shree Siddheshwar Hallmark,<br />
                      Nr, Ajwa Rd, Sayaji Park Society,<br />
                      Vadodara, Gujarat 390019<br />
                      India
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Newsletter Subscription Section */}
        <Box sx={{ mt: 8 }}>
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom>
              Stay Updated with InspectIQ
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Subscribe to our newsletter for the latest industry insights, company updates, and exclusive offers.
            </Typography>

            <form onSubmit={handleNewsletterSubmit}>
              <Grid container spacing={2} justifyContent="center" alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Name (Optional)"
                    name="name"
                    value={newsletterData.name}
                    onChange={handleNewsletterChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={newsletterData.email}
                    onChange={handleNewsletterChange}
                    required
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={newsletterLoading}
                    sx={{ py: 1.5 }}
                  >
                    {newsletterLoading ? <CircularProgress size={20} /> : 'Subscribe'}
                  </Button>
                </Grid>
              </Grid>
            </form>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              We respect your privacy. Unsubscribe at any time.
            </Typography>
          </Card>
        </Box>
      </Container>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;